import React, { useState, useEffect, useRef } from "react";
import {
  callOpenAI,
  generateHighlight,
  generateDetailedSummary,
  generateMetadataFromReflection,
} from "../functions/openai.js";
import LottieAnimation from "../components/LottieAnimation.js";
import reflect from "../assets/animations/reflect.json";
import reflecting from "../assets/animations/reflect.json";
import { ReflectHeading } from "../components/ReflectHeading";
import { useNavigate } from "react-router-dom";
import { createReflection } from "../functions/api";
import ShareReflectionModal from "../components/ShareReflectionModal";
import { reflectSystemPrompt } from "../utils/openAiPrompt";
import { trackAction } from "../utils/umami";
import { useDictation } from "../hooks/useDictation";

const MicIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="9" y="3" width="6" height="11" rx="3" />
    <path d="M6 11a6 6 0 0 0 12 0" />
    <line x1="12" y1="17" x2="12" y2="20.5" />
    <line x1="9" y1="20.5" x2="15" y2="20.5" />
  </svg>
);

const MicButton = ({ recording, transcribing, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={transcribing}
    aria-label={recording ? "Stop recording" : "Speak your reflection"}
    title={recording ? "Tap to stop" : "Speak your reflection"}
    className={`shrink-0 w-11 h-11 rounded-full flex items-center justify-center shadow-sm transition ${recording
      ? "bg-[#f3d9d4] text-[#b0564a] animate-pulse"
      : "bg-[#ece8e1] text-[#5e5a55] hover:bg-[#e4dfd6]"
      } ${transcribing ? "opacity-70 cursor-wait" : ""}`}
  >
    {transcribing ? (
      <span className="flex gap-0.5">
        {[0, 150, 300].map((d) => (
          <span
            key={d}
            className="w-1 h-1 rounded-full bg-current animate-pulse"
            style={{ animationDelay: `${d}ms` }}
          />
        ))}
      </span>
    ) : recording ? (
      <span className="w-3 h-3 rounded-[3px] bg-current" />
    ) : (
      <MicIcon />
    )}
  </button>
);

const ChipButton = ({ onClick, disabled = false, children }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs rounded-full border border-[#e2d8c5] bg-[#faf7f0] text-[#6e6861] hover:bg-[#f2ede2] hover:text-[#3c3a37] shadow-sm transition disabled:opacity-50 disabled:cursor-not-allowed"
  >
    {children}
  </button>
);

const DictationStatus = ({ recording, transcribing, error }) => {
  if (error) return <p className="text-xs text-[#b06a5f] mt-1">{error}</p>;
  if (transcribing) return <p className="text-xs text-[#8c8371] italic mt-1">Listening back…</p>;
  if (recording) return <p className="text-xs text-[#8c8371] italic mt-1">Recording… tap to stop when you're done.</p>;
  return null;
};

// ── Small line icons (same rounded style as the mic) ──────────────────────
const iconProps = {
  width: 15,
  height: 15,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.8,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true,
};
const IconBookmark = () => (
  <svg {...iconProps}><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" /></svg>
);
const IconRefresh = () => (
  <svg {...iconProps}>
    <polyline points="1 4 1 10 7 10" />
    <path d="M3.5 15a9 9 0 1 0 2.1-9.4L1 10" />
  </svg>
);
const IconHeart = () => (
  <svg {...iconProps}><path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1.1-1.1a5.5 5.5 0 1 0-7.8 7.8L12 21.2l8.8-8.8a5.5 5.5 0 0 0 0-7.8z" /></svg>
);
const IconBookOpen = () => (
  <svg {...iconProps}>
    <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
    <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
  </svg>
);
const SendIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="12" y1="20" x2="12" y2="5" />
    <polyline points="6 11 12 5 18 11" />
  </svg>
);

// ── Composer: a growing textarea with a tidy toolbar below ────────────────
const Composer = ({ value, onChange, onSend, loading, placeholder, dictation, big = false }: any) => {
  const taRef = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    const el = taRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  }, [value]);

  return (
    <div className="rounded-2xl border border-[#e4dcc9] bg-white/70 px-3.5 pt-3 pb-2 shadow-sm focus-within:ring-2 focus-within:ring-[#e0d6bf] transition">
      <textarea
        ref={taRef}
        rows={1}
        value={value}
        placeholder={placeholder}
        onChange={onChange}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            if (!loading && value.trim()) onSend();
          }
        }}
        className={`w-full resize-none bg-transparent outline-none placeholder-[#a89f8c] leading-relaxed max-h-40 ${big ? "text-base" : "text-[0.95rem]"}`}
      />
      <div className="flex items-center justify-between mt-1.5">
        {dictation.supported ? (
          <MicButton
            recording={dictation.recording}
            transcribing={dictation.transcribing}
            onClick={dictation.toggle}
          />
        ) : (
          <span />
        )}
        <button
          onClick={onSend}
          disabled={loading || !value.trim()}
          aria-label="Reflect"
          className="inline-flex items-center gap-1.5 bg-[#3c3a37] text-[#f8f4ef] pl-4 pr-3.5 py-2 rounded-full text-sm font-medium hover:bg-[#2e2c2a] transition shadow-sm disabled:opacity-40"
        >
          Reflect <SendIcon />
        </button>
      </div>
      <DictationStatus
        recording={dictation.recording}
        transcribing={dictation.transcribing}
        error={dictation.error}
      />
    </div>
  );
};

const ReflectChatPage = () => {
  const navigate = useNavigate();
  const userId =
    localStorage.getItem("breatheUserId") || /* rare */ "unknown_user";

  const [messages, setMessages] = useState(() => {
    return (
      JSON.parse(localStorage.getItem("breatheChatHistory")) || [
        {
          role: "assistant",
          content: "What happened today?",
        },
      ]
    );
  });
  const [input, setInput] = useState("");
  const [hasTyped, setHasTyped] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasStarted, setHasStarted] = useState(messages.length > 1);
  const [showShareModal, setShowShareModal] = useState(false);
  const [reflectionMetadata, setReflectionMetadata] = useState({
    reflection: "",
    alias: "",
    feeling: "",
    tags: [],
  });
  const [loadingMetadata, setLoadingMetadata] = useState(false);
  const [submittingReflection, setSubmittingReflection] = useState(false);
  const [bounceIndex, setBounceIndex] = useState(null);

  const endRef = useRef(null);

  const dictation = useDictation((text) => {
    trackAction("Reflect - Voice transcript added");
    setHasTyped(true);
    setInput((prev) => (prev.trim() ? `${prev.trim()} ${text}` : text));
  });

  useEffect(() => {
    localStorage.setItem("breatheChatHistory", JSON.stringify(messages));
  }, [messages]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  // useEffect(() => {
  //   const interval = setInterval(() => {
  //     const randomIndex = Math.floor(Math.random() * 2); // 0, 1, or 2
  //     setBounceIndex(randomIndex);

  //     setTimeout(() => setBounceIndex(null), 3000); // remove after animation
  //   }, 3000); // 6-9s random interval

  //   return () => clearInterval(interval);
  // }, []);

  const handleSend = async () => {
    trackAction("Reflect - Send to Chat")
    if (!input.trim()) return;
    const userMessage = input.trim();
    setInput("");
    setLoading(true);
    setHasStarted(true);

    const newMessages = [...messages, { role: "user", content: userMessage }];
    setMessages(newMessages);

    const systemPrompt = reflectSystemPrompt;

    const finalMessages = [systemPrompt, ...newMessages.slice(-4)];

    const aiText = await callOpenAI(finalMessages);
    if (aiText) trackAction("Reflect - AI Replied")
    const updatedMessages = [
      ...newMessages,
      { role: "assistant", content: aiText },
    ];
    setMessages(updatedMessages);
    setLoading(false);
  };

  const handleStartFresh = () => {
    trackAction("Reflect - Start Fresh")
    const freshStart = [
      {
        role: "assistant",
        content: "What happened today?",
      },
    ];
    setMessages(freshStart);
    setHasStarted(false);
    setInput("");
    localStorage.setItem("breatheChatHistory", JSON.stringify(freshStart));
  };

  const handleSaveReflection = async () => {
    trackAction("Reflect - Save Reflection")
    setSaving(true);
    const convoMessages = messages.filter((m) => m.role === "user");
    const highlight = await generateHighlight(convoMessages);
    const aiSummary = await generateDetailedSummary(convoMessages);

    if (highlight && aiSummary) trackAction("Reflect - AI Reflection Saved")

    const newSummary = {
      date: new Date().toISOString(),
      highlight,
      aiSummary,
    };

    const stored = JSON.parse(localStorage.getItem("breatheArchive")) || [];
    const updated = [newSummary, ...stored];
    localStorage.setItem("breatheArchive", JSON.stringify(updated));

    // Open the book to the page we just wrote (newest = last leaf).
    localStorage.setItem("breatheBookPage", String(updated.length - 1));

    setSaving(false);
    navigate("/storybook");
  };

  const handleShareWithPeers = async () => {
    trackAction("Reflect - Share with Peers")
    const convoMessages = messages.filter((m) => m.role === "user");
    setShowShareModal(true);
    setLoadingMetadata(true);
    const metadata = await generateMetadataFromReflection(convoMessages);
    trackAction("Reflect - Metadata generated")
    setReflectionMetadata(metadata);
    setLoadingMetadata(false);
    // setShowShareModal(true);
  };

  const handleShareSubmit = async () => {
    trackAction("Reflect - Submit Reflection clicked")
    setSubmittingReflection(true);
    try {
      const { reflection, alias, feeling, tags } = reflectionMetadata;
      const reflectionData = {
        userId,
        alias,
        feeling,
        reflection,
        tags,
      };

      await createReflection(reflectionData);
      trackAction("Reflect - Submit Reflection success")

      // alert("Your reflection has been shared with peers. 🌱");
      setSubmittingReflection(false);
      setShowShareModal(false);
      navigate("/connect?section=your-reflections"); // Navigate to Peer Room -- Your Reflections section
    } catch (error) {
      console.error("Error sharing reflection:", error);
      alert(`Failed to share reflection. Please try again.`);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInput(value);

    if (!hasTyped && value.trim() !== "") {
      trackAction("Reflect - User started typing");
      setHasTyped(true);
    }
  };

  return (
    <div className="flex flex-col w-full max-w-2xl mx-auto px-4 sm:px-5">
      {/* Heading — loud in the empty state, quiet once you're writing.
          mt clears the fixed floating-leaf button at top-center. */}
      <div className="flex flex-col items-center mt-16 sm:mt-10 mb-4">
        <ReflectHeading compact={hasStarted} showQuote={!hasStarted} />
      </div>

      {/* Conditional Content */}
      {hasStarted ? (
        <>
          {/* Chat Container — a quiet conversation on paper */}
          <LottieAnimation animation={reflecting} opacity={0.16} scale={1} zIndex={-1} />
          <div className="flex-1 flex flex-col rounded-2xl bg-[#fdfbf5] border border-[#e9e0cd] shadow-[0_12px_34px_-20px_rgba(60,50,35,0.35)] overflow-hidden">
            {/* Scrollable Messages */}
            <div className="flex-1 overflow-y-auto px-5 py-6 space-y-5 max-h-[46vh] scrollbar-hide">
              {messages.map((msg, idx) =>
                msg.role === "user" ? (
                  <div
                    key={idx}
                    className="max-w-[78%] ml-auto rounded-2xl rounded-br-md bg-[#efe9dc] text-[#3c3a37] px-4 py-2.5 text-[0.95rem] leading-relaxed whitespace-pre-line shadow-sm"
                  >
                    {msg.content}
                  </div>
                ) : (
                  <div
                    key={idx}
                    className="max-w-[88%] mr-auto border-l-2 border-[#ddd0b3] pl-4 font-serif italic text-[1.05rem] leading-[1.75] text-[#4a463f]"
                  >
                    {msg.content
                      .split("\n")
                      .filter((line) => line.trim() !== "")
                      .map((line, i) => {
                        const isNewest = idx === messages.length - 1;
                        return (
                          <p
                            key={i}
                            className={`${i > 0 ? "mt-2" : ""} ${isNewest ? "animate-fade-in" : ""}`}
                            style={isNewest ? { animationDelay: `${i * 0.45}s`, animationFillMode: "both" } : undefined}
                          >
                            {line}
                          </p>
                        );
                      })}
                  </div>
                )
              )}
              {loading && (
                <div className="flex items-center gap-1.5 pl-1" aria-label="reflecting">
                  {[0, 200, 400].map((d) => (
                    <span
                      key={d}
                      className="w-1.5 h-1.5 rounded-full bg-[#c9bfa8] animate-pulse"
                      style={{ animationDelay: `${d}ms` }}
                    />
                  ))}
                </div>
              )}
              <div ref={endRef} />
            </div>

            {/* Input Field */}
            <div className="border-t border-[#eee5d3] px-3 py-3 bg-[#f8f5ee]">
              <Composer
                value={input}
                onChange={handleInputChange}
                onSend={handleSend}
                loading={loading}
                placeholder="Keep sharing here, softly…"
                dictation={dictation}
              />
            </div>
          </div>

          {/* Quiet actions, resting below the conversation */}
          <div className="flex justify-center flex-wrap gap-2 mt-4">
            <ChipButton onClick={handleSaveReflection} disabled={saving}>
              <span style={{ color: "#a9673f" }}><IconBookmark /></span>
              {saving ? "Binding the page…" : "Add to my book"}
            </ChipButton>
            <ChipButton onClick={handleStartFresh}>
              <span style={{ color: "#5d7895" }}><IconRefresh /></span>
              Begin again
            </ChipButton>
            <ChipButton onClick={handleShareWithPeers} disabled={loadingMetadata}>
              <span style={{ color: "#9c5a6a" }}><IconHeart /></span>
              {loadingMetadata ? "Preparing…" : "Offer to someone"}
            </ChipButton>
          </div>
        </>
      ) : (
        // Welcome Section
        <div className="flex-1 flex flex-col items-center justify-center space-y-7 py-10">
          <LottieAnimation animation={reflect} opacity={0.22} zIndex={-1} />
          <h1
            className="text-center text-[1.9rem] sm:text-4xl text-[#3c3a37] max-w-md leading-tight tracking-[0.01em] font-normal"
            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
          >
            What happened today?
          </h1>
          <div className="w-full max-w-md">
            <Composer
              big
              value={input}
              onChange={handleInputChange}
              onSend={handleSend}
              loading={loading}
              placeholder="Tell it what happened today…"
              dictation={dictation}
            />
          </div>
        </div>
      )}

      {/* Back to Storybook Button */}
      <div className="mt-6 flex justify-center">
        <button
          onClick={() => {
            trackAction("Reflect - Back to Book");
            navigate("/storybook");
          }}
          className="inline-flex items-center gap-2 bg-[#f1ece4] hover:bg-[#e7e2db] text-[#3c3a37] font-medium px-6 py-2 rounded-full transition shadow-sm hover:shadow-md"
        >
          <span style={{ color: "#a9673f" }}><IconBookOpen /></span>
          Back to my book
        </button>
      </div>

      {/* Share Modal */}
      <ShareReflectionModal
        isOpen={showShareModal}
        onClose={() => {
          trackAction("Share with Peers - Cancel");
          setShowShareModal(false)
        }}
        onSubmit={handleShareSubmit}
        submitting={submittingReflection}
        reflectionMetadata={reflectionMetadata}
        setReflectionMetadata={setReflectionMetadata}
        loadingMetadata={loadingMetadata}
      />
    </div >
  );
};

export default ReflectChatPage;
